import { ChangeDetectorRef, Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PageLoaderService } from '../../../core/service/page-loader.service';
import { Images } from '../../../shared/models/file.model';
import { ProductsService } from '../../../shared/services/products.service';

@Component({
  selector: 'app-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  standalone: false,
})
export class ProductCardComponent implements OnInit, OnDestroy {
  images: Images[] = [];
  chunkedImages: Images[][] = [];
  public loadingContent = true;
  public hasError = false;
  public errorMessage = '';
  product!: string;
  hasMultipleSlides = false;
  @Output() enableProductOverview = new EventEmitter<boolean>(false);
  private imagesSubscription?: Subscription;
  private productSubscription?: Subscription;

  constructor(
    private productService: ProductsService,
    private router: Router,
    private pageLoaderService: PageLoaderService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.loadImages();
    this.subscribeToCurrentProduct();
    this.onResize();
  }

  ngOnDestroy(): void {
    this.imagesSubscription?.unsubscribe();
    this.productSubscription?.unsubscribe();
  }

  private loadImages(): void {
    this.hasError = false;
    this.errorMessage = '';
    this.loadingContent = true;
    this.imagesSubscription?.unsubscribe();

    this.imagesSubscription = this.productService.getImages().subscribe({
      next: (data: Images[]) => {
        this.images = data || [];
        this.chunkImages();
        this.loadingContent = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        console.error('An error occurred during getting images:', err);
        this.hasError = true;
        if (err.status === 429) {
          this.errorMessage = this.translate.instant('PRODUCTS.CARD.RATE_LIMIT_ERROR');
        } else if (err.status === 0) {
          this.errorMessage = this.translate.instant('PRODUCTS.CARD.NETWORK_ERROR');
        } else {
          this.errorMessage = this.translate.instant('PRODUCTS.CARD.LOAD_FAILED');
        }
        this.images = [];
        this.chunkedImages = [];
        this.loadingContent = false;
        this.cdr.detectChanges();
      },
    });

    this.pageLoaderService.showSpinnerForDataLoading(this.imagesSubscription);
  }

  private subscribeToCurrentProduct(): void {
    this.productSubscription = this.productService.currentProduct$.subscribe({
      next: (getProducts: string) => {
        this.product = getProducts;
      },
      error: (err: Error) => {
        console.error('An error occurred during getting current product:', err);
      },
    });
    this.chunkImages();
  }

  getItemsPerSlide(): number {
    const width = window.innerWidth;
    if (width < 768) {
      return 1; // Mobile: 1 item
    }
    if (width < 992) {
      return 2; // Tablet: 2 items
    }
    return 3; // Desktop: 3 items
  }

  getColumnClass(chunkLength: number): string {
    const width = window.innerWidth;

    if (width < 768) {
      return 'col-12';
    }

    if (width < 992) {
      return chunkLength === 1 ? 'col-6 offset-3' : 'col-6';
    }

    switch (chunkLength) {
      case 1:
        return 'col-4 offset-4';
      case 2:
        return 'col-4 offset-2';
      default:
        return 'col-4';
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?: Event) {
    this.chunkImages();
  }

  chunkImages(): void {
    if (this.images && this.images.length > 0) {
      const chunkSize = window.innerWidth < 961 ? 1 : 3;
      this.chunkedImages = this.chunkArray(this.images, chunkSize);
      // Update hasMultipleSlides after chunking
      this.hasMultipleSlides = this.chunkedImages.length > 1;
    } else {
      this.chunkedImages = [];
      this.hasMultipleSlides = false;
    }
  }

  enableProduct(enableProductOverview: boolean): void {
    this.enableProductOverview.emit(enableProductOverview);
  }

  goToProductDetails(image: Images): void {
    const productId = image.id;
    const dryerGroup = image.name;
    this.productService.setSelectedDryerGroup(dryerGroup);
    this.router.navigate([`/product/${productId}`]);
  }

  retryLoadImages(): void {
    this.loadImages();
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    target.src = 'assets/images/placeholder.jpg';
  }

  chunkArray(array: Images[], chunkSize: number): Images[][] {
    const chunks: Images[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  get hasValidData(): boolean {
    return !this.hasError && !this.loadingContent && this.images.length > 0;
  }

  get showEmptyState(): boolean {
    return !this.loadingContent && !this.hasError && this.images.length === 0;
  }
}
